import type {ReactNode} from 'react'
import {Component, use} from 'react'
import {AnalyticsContext} from '@github-ui/analytics-provider/context'
// eslint-disable-next-line no-restricted-imports
import {reportError} from '@github-ui/failbot'
import {isFeatureEnabled} from '@github-ui/feature-flags'

export type PreloadedQueryBoundaryProps = {
  children: ReactNode
  onRetry: () => void
  fallback?: (retry: () => void, error: Error) => ReactNode
  critical?: boolean
  appName?: string
}

type State = {
  error: Error | null
  hasAutoRetried: boolean
}

const defaultFallBack = (retry: () => void) => {
  return (
    <div>
      <span>Error:</span>
      <button onClick={retry}>Retry</button>
    </div>
  )
}

export type ReportableError = Error & {shouldSkipReport?: boolean}

export class BasicPreloadedQueryBoundary extends Component<PreloadedQueryBoundaryProps, State> {
  override state: State = {error: null, hasAutoRetried: false}

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Mark error to skip React 19's onCaughtError reporting - we handle it ourselves in componentDidCatch
    ;(error as ReportableError).shouldSkipReport = true
    return {error}
  }

  override componentDidCatch(error: ReportableError) {
    const featureFlagEnabled = isFeatureEnabled('issues_react_auto_retry_on_error')
    const willRetry = featureFlagEnabled && !this.state.hasAutoRetried

    reportError(error, {
      critical: willRetry ? false : (this.props.critical ?? false),
      reactAppName: this.props.appName,
      reactErrorBoundaryName: 'BasicPreloadedQueryBoundary',
    })

    if (willRetry) {
      this.props.onRetry()
      this.setState({hasAutoRetried: true, error: null})
    }
  }

  _retry = () => {
    // This ends up calling loadQuery again to get and render
    // a new query reference
    this.props.onRetry()
    this.setState({
      // Clear the error
      error: null,
      hasAutoRetried: false,
    })
  }

  override render() {
    const {children} = this.props
    const {error} = this.state
    const fallback = this.props.fallback || defaultFallBack

    if (error) {
      return fallback(this._retry, error)
    }

    return children
  }
}

export default function PreloadedQueryBoundary(props: PreloadedQueryBoundaryProps) {
  const {appName: propsAppName} = props
  const context = use(AnalyticsContext)
  const appName = propsAppName || context?.appName
  return <BasicPreloadedQueryBoundary {...props} appName={appName} />
}

try{ PreloadedQueryBoundary.displayName ||= 'PreloadedQueryBoundary' } catch {}